import { z } from 'zod'
import { IOTypeSchema } from '../widget/shared'
import { OptionalDateRangeSchema } from '../time'
import { RoleIdSchema } from '../role'
import { ReportColumnDisplaySchema } from '../report/column'
import { LatLngSchema } from '../locations/latLng'
import {
  AccountStatusIdSchema,
  CompanyIdSchema,
  TeamIdSchema,
  UserIdSchema,
  WithWorkflowIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
  type CompanyId,
  type LocationId,
  type TeamId,
  type UserId,
  type WorkflowActionId,
  type WorkflowId,
  type WorkflowStageId,
} from '../ids'
import { EventOutcomeIdSchema } from '../eventOutcome'
import { TerrosEntitlementSchema } from '../entitlements'
import { type SortTimestamp } from '../elasticApi'
import { DispositionIdSchema } from '../disposition'
import { CustomFieldFilterSchema } from '../customFieldMap'
import { AuditHistorySchema, AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'
import { AccountStatusScopeSchema, RequiredAccountFieldSchema, SetterCloserConfigSchema } from '../accountStatus'
import { LauncherConfigSchema } from './launcher'

export const WORKFLOW_PROSPECT_STAGE_ID: WorkflowStageId = 'S.Prospect'

/** Placeholder action ID used when a workflow history item has no actionId (stage-only change) */
export const WORKFLOW_STAGE_CHANGE_ACTION_ID: WorkflowActionId = 'A.Transition'

export const WorkflowEntityIdSchema = z.union([CompanyIdSchema, TeamIdSchema, RoleIdSchema])
export type WorkflowEntityId = z.Infer<typeof WorkflowEntityIdSchema>

const UndoActionPreferencesSchema = z.object({
  distanceFromLocation: z.number().int().nonnegative().optional(),
  timeSince: z.number().int().nonnegative().optional().meta({
    description:
      'Minutes since the last activity after which the action can no longer be undone; 0 turns the time check off. A duration, so no time zone applies.',
  }),
})

export type UndoActionPreferences = z.infer<typeof UndoActionPreferencesSchema>

const WorkflowScheduleRestrictionSchema = z.object({
  operator: z.enum(['gte', 'lte']),
  milliseconds: z.number().int().nonnegative(),
})
export type WorkflowScheduleRestriction = z.infer<typeof WorkflowScheduleRestrictionSchema>

const WorkflowEventConfigSchema = z.object({
  scheduleAppointment: z.boolean().optional(),
  appointmentType: z.enum(['personal', 'closer']).optional(),
  scheduleRestriction: WorkflowScheduleRestrictionSchema.optional().describe(
    'User can only schedule closer events in this time window. User with event:manage permission can bypass this restriction.'
  ),
})
export type WorkflowEventConfig = z.infer<typeof WorkflowEventConfigSchema>

export const WorkflowNoteConfigSchema = z.object({
  required: z.boolean().optional().describe('When enabled, users must add a note when performing this action'),
  timeframeMs: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('If a note was left within this timeframe (ms), skip the requirement. Defaults to 24 hours'),
})
export type WorkflowNoteConfig = z.infer<typeof WorkflowNoteConfigSchema>

const WorkflowActionAccountConfigSchema = z.object({
  ...WorkflowEventConfigSchema.shape,
  trackUserLocation: z.boolean().optional().describe("Whether this action requires tracking the user's location"),
  undoAction: UndoActionPreferencesSchema.optional(),
  noteConfig: WorkflowNoteConfigSchema.optional().describe('Note requirement configuration for this action'),
})

const WorkflowActionEventConfigSchema = z.object({
  occurred: z.boolean().optional(),
  reschedule: z.boolean().optional(),
  closed: z.boolean().optional(),
  show: z.enum(['before', 'after']).optional(),
  fault: z.enum(['setter', 'closer']).optional(),
})

const WorkflowAiConfigSchema = z.object({
  enabled: z.boolean().optional(),
  shouldGrade: z.boolean().optional().describe('Whether this action should trigger grading of the conversation by AI'),
  prompt: z.string().optional().describe('The prompt to use for the AI when evaluating this action'),
})
export type WorkflowAiConfig = z.infer<typeof WorkflowAiConfigSchema>

export const WorkflowStageAiConfigSchema = z.object({
  ...WorkflowAiConfigSchema.shape,
  enabled: z
    .boolean()
    .optional()
    .describe('When true, AI classification is enabled for this stage. Ignored when `flagRecordings` is true.'),
  flagRecordings: z
    .boolean()
    .optional()
    .describe(
      'When true, recordings made at a location with an account in this stage are flagged, and AI classification (`enabled`) is ignored for this stage. Off by default.'
    ),
})
export type WorkflowStageAiConfig = z.infer<typeof WorkflowStageAiConfigSchema>

export const REPORT_ON_OPTIONS = ['contributor', 'actor'] as const
export const ReportOnSchema = z.enum(REPORT_ON_OPTIONS)
export type ReportOn = z.infer<typeof ReportOnSchema>

export const WORKFLOW_STATS_VARIANTS = ['none', 'setter', 'closer', 'both'] as const
export const WorkflowStatsVariantSchema = z.enum(WORKFLOW_STATS_VARIANTS)
export type WorkflowStatsVariant = z.infer<typeof WorkflowStatsVariantSchema>

export const WorkflowReportConfigSchema = z.object({
  count: z.boolean().optional(),
  daysSince: z.boolean().optional(),
  timeOfDay: z.boolean().optional(),
  hoursWorked: z.object({ enabled: z.boolean() }).optional(),
  /** Which users on the stage document to attribute stats to. Defaults to ['contributor']. */
  reportOn: ReportOnSchema.array().optional(),
  variantsShown: WorkflowStatsVariantSchema.optional(),
})

export type WorkflowReportConfig = z.infer<typeof WorkflowReportConfigSchema>

const DEFAULT_REPORT_ON: ReportOn[] = ['contributor']

export function getReportOn(reportConfig?: WorkflowReportConfig): ReportOn[] {
  return reportConfig?.reportOn ?? DEFAULT_REPORT_ON
}

const OrderSchema = z.number().int().nonnegative()

export const WORKFLOW_CONDITION_COUNT_OPERATORS = ['eq', 'lt', 'lte', 'gt', 'gte'] as const
const WorkflowConditionCountOperatorSchema = z.enum(WORKFLOW_CONDITION_COUNT_OPERATORS)
export const isWorkflowConditionCountOperator = (value: unknown): value is WorkflowConditionCountOperator =>
  WorkflowConditionCountOperatorSchema.safeParse(value).success
export type WorkflowConditionCountOperator = z.infer<typeof WorkflowConditionCountOperatorSchema>

export const WorkflowConditionActionCountSchema = z.object({
  type: z.literal('action_count'),
  actionId: WorkflowActionIdSchema.optional(),
  count: z.number().int().nonnegative(),
  operator: WorkflowConditionCountOperatorSchema.default('gte'),
})
export type WorkflowConditionActionCount = z.infer<typeof WorkflowConditionActionCountSchema>

export const WorkflowConditionDistanceFromPropertySchema = z.object({
  type: z.literal('distance_from_property'),
  meters: z.number().int().positive(),
})
export type WorkflowConditionDistanceFromProperty = z.infer<typeof WorkflowConditionDistanceFromPropertySchema>

export const WorkflowConditionDistanceFromActionSchema = z.object({
  type: z.literal('distance_from_action'),
  on: z.enum(['this', 'any']),
  meters: z.number().int().positive(),
  actionId: WorkflowActionIdSchema.optional().describe(
    'The action to measure distance from, defaults to the current action'
  ),
})
export type WorkflowConditionDistanceFromAction = z.infer<typeof WorkflowConditionDistanceFromActionSchema>

export const WorkflowConditionTimeSinceLastActionSchema = z.object({
  type: z.literal('time_since'),
  on: z.enum(['this', 'any']),
  milliseconds: z.number().int().positive(),
  actionId: WorkflowActionIdSchema.optional().describe(
    'The action to measure distance from, defaults to the current action'
  ),
})
export type WorkflowConditionTimeSinceLastAction = z.infer<typeof WorkflowConditionTimeSinceLastActionSchema>

export const WorkflowConditionTimeOfDaySchema = z
  .object({
    type: z.literal('time_of_day'),
    minutesStart: z.number().int().min(0).max(1439).describe('Start time in minutes from midnight (0-1439)'),
    minutesEnd: z.number().int().min(0).max(1439).describe('End time in minutes from midnight (0-1439)'),
  })
  .refine((data) => data.minutesStart < data.minutesEnd, {
    message: 'minutesStart must be less than minutesEnd',
    path: ['minutesStart'],
  })
export type WorkflowConditionTimeOfDay = z.infer<typeof WorkflowConditionTimeOfDaySchema>

export const WorkflowConditionSchema = z.discriminatedUnion('type', [
  WorkflowConditionActionCountSchema,
  WorkflowConditionDistanceFromPropertySchema,
  WorkflowConditionDistanceFromActionSchema,
  WorkflowConditionTimeSinceLastActionSchema,
  WorkflowConditionTimeOfDaySchema,
])
export type WorkflowCondition = z.infer<typeof WorkflowConditionSchema>

export type WorkflowConditionType = WorkflowCondition['type']

const CheckpointBehaviorSchema = z.enum(['always', 'ask', 'never'])
export type CheckpointBehavior = z.infer<typeof CheckpointBehaviorSchema>
const StageCheckpointBehaviorSchema = z.object({
  behavior: CheckpointBehaviorSchema.optional(),
})

const StageCheckpointMapSchema = z.record(WorkflowStageIdSchema, StageCheckpointBehaviorSchema)
export type StageCheckpointMap = z.infer<typeof StageCheckpointMapSchema>

export const HOOK_BEHAVIORS = ['ask', 'always'] as const
export const HookBehaviorSchema = z.enum(HOOK_BEHAVIORS)
export type HookBehavior = z.infer<typeof HookBehaviorSchema>
export const isHookBehavior = (value: unknown): value is HookBehavior => HookBehaviorSchema.safeParse(value).success

export const PreHookTypeSchema = z.enum(['action'])
export type PreHookType = z.infer<typeof PreHookTypeSchema>

export const WorkflowPreHookSchema = z.object({
  type: PreHookTypeSchema,
  actionId: WorkflowActionIdSchema,
  behavior: HookBehaviorSchema,
})
export type WorkflowPreHook = z.infer<typeof WorkflowPreHookSchema>

const UserPostHookSchema = z.object({
  type: z.literal('user'),
  behavior: z.literal('always'),
  action: z.enum(['create', 'archive']),
})
export type UserPostHook = z.infer<typeof UserPostHookSchema>

const LauncherPostHookSchema = z.object({
  type: z.literal('launch'),
  behavior: HookBehaviorSchema,
  launcher: LauncherConfigSchema,
})
export type LauncherPostHook = z.infer<typeof LauncherPostHookSchema>

export const WorkflowPostHookSchema = z.discriminatedUnion('type', [LauncherPostHookSchema, UserPostHookSchema])
export type WorkflowPostHook = z.infer<typeof WorkflowPostHookSchema>

export const WorkflowActionHooksSchema = z.object({
  pre: WorkflowPreHookSchema.array().optional(),
  post: WorkflowPostHookSchema.array().optional(),
})
export type WorkflowActionHooks = z.infer<typeof WorkflowActionHooksSchema>

export const WorkflowStageHooksSchema = z.object({
  post: WorkflowPostHookSchema.array().optional(),
})
export type WorkflowStageHooks = z.infer<typeof WorkflowStageHooksSchema>

export const WorkflowActionSchema = z.object({
  actionId: WorkflowActionIdSchema,
  legacyId: z.union([DispositionIdSchema, EventOutcomeIdSchema]).optional(),
  toStageId: WorkflowStageIdSchema.optional(),
  fromStages: z.array(WorkflowStageIdSchema).meta({ deprecated: true }),
  name: z.string().nonempty(),
  description: z.string().optional(),
  short: z.string().optional(),
  plural: z.string().optional(),
  icon: z.string(),
  color: z.string().meta({
    description:
      'Hex color code for the action display. Defaults to the target stage color if not set. Empty string means no color override.',
  }),
  conditions: WorkflowConditionSchema.array().optional(),
  aiConfig: WorkflowAiConfigSchema.optional().meta({ deprecated: true }),
  accountConfig: WorkflowActionAccountConfigSchema.optional(),
  eventConfig: WorkflowActionEventConfigSchema.optional(),
  reportConfig: WorkflowReportConfigSchema.optional(),
  checkpoints: StageCheckpointMapSchema.optional().describe(
    'Legacy, replaced by `hooks.pre`. Map of stageId to `{ behavior: "always" | "ask" | "never" }`: before this action, the account passes through these stages as stage-only history entries, so no action is credited for them.'
  ),
  hooks: WorkflowActionHooksSchema.optional().describe(
    'Hooks to execute around this action. `pre` is an array of `{ type: "action", actionId, behavior: "always" | "ask" }`: actions recorded just before this one, each credited and condition-checked as its own action.'
  ),
  roles: RoleIdSchema.array().optional(),
  order: OrderSchema.optional().meta({ description: 'Order of the action in the workflow' }),
  ...ArchivableSchema.shape,
  ...AuditHistorySchema.shape,
})
export type WorkflowActionData = z.infer<typeof WorkflowActionSchema>

const WorkflowAutoTriggerSchema = z.object({
  hours: z.number().int().nonnegative().optional(),
  days: z.number().int().nonnegative().optional(),
  years: z.number().int().nonnegative().optional(),
  stageId: WorkflowStageIdSchema,
})
export type WorkflowAutoTrigger = z.infer<typeof WorkflowAutoTriggerSchema>

export const DATE_OFFSET_UNITS = ['hour', 'day', 'week', 'month'] as const
export const DateOffsetUnitSchema = z.enum(DATE_OFFSET_UNITS)
export type DateOffsetUnit = z.infer<typeof DateOffsetUnitSchema>
export const isDateOffsetUnit = (value: unknown): value is DateOffsetUnit =>
  DateOffsetUnitSchema.safeParse(value).success

export const DATE_OFFSET_DIRECTIONS = ['on', 'before', 'after'] as const
export const DateOffsetDirectionSchema = z.enum(DATE_OFFSET_DIRECTIONS)
export type DateOffsetDirection = z.infer<typeof DateOffsetDirectionSchema>
export const isDateOffsetDirection = (value: unknown): value is DateOffsetDirection =>
  DateOffsetDirectionSchema.safeParse(value).success

const DateOffsetOnSchema = z.object({
  direction: z.literal('on'),
  unit: DateOffsetUnitSchema,
})
export type DateOffsetOn = z.infer<typeof DateOffsetOnSchema>

const DateOffsetBeforeAfterSchema = z.object({
  direction: z.enum(['before', 'after']),
  value: z.number().int().positive().describe('Number of time units'),
  unit: DateOffsetUnitSchema.describe('Time unit for the offset'),
})
export type DateOffsetBeforeAfter = z.infer<typeof DateOffsetBeforeAfterSchema>

export const DateOffsetSchema = z.discriminatedUnion('direction', [DateOffsetOnSchema, DateOffsetBeforeAfterSchema])
export type DateOffset = z.infer<typeof DateOffsetSchema>

export const TASK_ASSIGN_TO_OPTIONS = ['owner', 'closer', 'actionRoles'] as const
export const TaskAssignToSchema = z.enum(TASK_ASSIGN_TO_OPTIONS)
export type TaskAssignTo = z.infer<typeof TaskAssignToSchema>
export const isTaskAssignTo = (value: unknown): value is TaskAssignTo => TaskAssignToSchema.safeParse(value).success

const WorkflowTaskConfigSchema = z.object({
  required: z.boolean().optional().meta({ description: 'Whether the task is required, by default its optional' }),
  priority: z.number().int().nonnegative().optional(),
  assignTo: TaskAssignToSchema.optional().meta({
    description: 'Who the task will be assigned to, by default its actionRoles',
  }),
  dateField: z.union([z.literal('appointmentDate'), WorkflowActionIdSchema, WorkflowStageIdSchema]).optional(),
  dateOffset: DateOffsetSchema.optional().describe('Time offset configuration relative to the dateField'),
})
export type WorkflowTaskConfig = z.infer<typeof WorkflowTaskConfigSchema>

/** @deprecated Use WorkflowEventConfig instead */
const WorkflowStageEventConfigSchema = WorkflowEventConfigSchema
/** @deprecated Use WorkflowEventConfig instead */
export type WorkflowStageEventConfig = WorkflowEventConfig
/** @deprecated Use WorkflowScheduleRestriction instead */
export type WorkflowStageScheduleRestriction = WorkflowScheduleRestriction

export const WORKFLOW_STAGE_WHO_OPTIONS = ['none', 'setter', 'closer'] as const
export const WorkflowStageWhoSchema = z.enum(WORKFLOW_STAGE_WHO_OPTIONS)
export type WorkflowStageWho = z.infer<typeof WorkflowStageWhoSchema>
export const NEVER_REPEAT_CREDIT_AFTER_DAYS = -1

export const WorkflowStageAdvancedConfigSchema = z.object({
  actionCredit: z.boolean().optional().meta({
    description:
      'When disabled, the person who took the action that moved the entity to this stage does not receive credit for it',
  }),
  changePrimaryOwnership: z
    .boolean()
    .optional()
    .meta({ description: 'Whether this stage changes primary ownership of the entity' }),
  futurePipelineCredit: z.boolean().optional().meta({
    description:
      'When enabled, the person who took the action that moved the entity to this stage receives credit for all future pipeline stages in the workflow from that point on',
  }),
  who: WorkflowStageWhoSchema.optional(),
  allowCreditAfterDays: z.number().int().min(NEVER_REPEAT_CREDIT_AFTER_DAYS).optional(),
})
export type WorkflowStageAdvancedConfig = z.infer<typeof WorkflowStageAdvancedConfigSchema>

export const WORKFLOW_STAGE_ROLLBACK_BEHAVIORS = ['target', 'protected'] as const
export const WorkflowStageRollbackBehaviorSchema = z.enum(WORKFLOW_STAGE_ROLLBACK_BEHAVIORS)
export type WorkflowStageRollbackBehavior = z.infer<typeof WorkflowStageRollbackBehaviorSchema>

export const WorkflowStageSchema = z.object({
  stageId: WorkflowStageIdSchema,
  legacyId: AccountStatusIdSchema.optional(),
  order: OrderSchema.optional().meta({ description: 'Order of the stage in the workflow' }),
  name: z.string(),
  description: z.string().optional(),
  autoTrigger: WorkflowAutoTriggerSchema.optional(),
  short: z.string().optional(),
  plural: z.string().optional(),
  icon: z.string().meta({ deprecated: true }),
  color: z.string(),
  restricted: z.boolean().optional(),
  // sorted available actions
  actionIds: WorkflowActionIdSchema.array().optional().describe('The outgoing actions from this stage'),
  // visibility scopes
  scope: AccountStatusScopeSchema,
  // quarterly plans
  setter: SetterCloserConfigSchema.optional(),
  closer: SetterCloserConfigSchema.optional(),
  selfgen: SetterCloserConfigSchema.optional(),
  // resident forms
  requiredFields: RequiredAccountFieldSchema.array().optional(),
  // widgets
  ioType: IOTypeSchema.optional(),
  aiConfig: WorkflowStageAiConfigSchema.optional(),
  // stats
  reportConfig: WorkflowReportConfigSchema.omit({ hoursWorked: true }).optional(),
  advancedConfig: WorkflowStageAdvancedConfigSchema.optional(),
  //tasks
  taskConfig: WorkflowTaskConfigSchema.optional(),
  // event
  eventConfig: WorkflowStageEventConfigSchema.optional(),
  noteConfig: WorkflowNoteConfigSchema.optional().describe(
    'Note requirement configuration for this stage. Can be overridden per action.'
  ),
  hooks: WorkflowStageHooksSchema.optional().describe('Hooks to execute around this stage'),
  rollback: WorkflowStageRollbackBehaviorSchema.optional().describe(
    'Rollback behavior for this stage. Unset = accounts in this stage can be rolled back.'
  ),
  ...ArchivableSchema.shape,
  ...AuditHistorySchema.shape,
})
export type WorkflowStageData = z.infer<typeof WorkflowStageSchema>

const WorkflowIntegrationMappingSchema = z.object({
  sourceStage: z.string(),
  targetId: z.union([WorkflowStageIdSchema, WorkflowActionIdSchema]),
})
export type WorkflowIntegrationMappingItem = z.infer<typeof WorkflowIntegrationMappingSchema>

export const WORKFLOW_ENTITY_TYPES = ['account', 'user', 'subAccount'] as const
export const WorkflowEntityTypeSchema = z.enum(WORKFLOW_ENTITY_TYPES).describe('The entity this workflow applies to')
export type WorkflowEntityType = z.infer<typeof WorkflowEntityTypeSchema>

const CloserEventFlowSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('residentFirst'),
  }),
  z.object({
    type: z.literal('timeslotFirst'),
  }),
])
export type CloserEventFlow = z.infer<typeof CloserEventFlowSchema>

export const WorkflowRollbackPreferencesSchema = z.object({
  enabled: z.boolean(),
  defaultStageId: WorkflowStageIdSchema.optional().describe(
    'Fallback when an account has no target stage in its history. Must be a target stage.'
  ),
  roleIds: RoleIdSchema.array().optional().describe('Roles allowed to roll back accounts'),
})
export type WorkflowRollbackPreferences = z.infer<typeof WorkflowRollbackPreferencesSchema>

export const WorkflowPreferencesSchema = z.object({
  undoAction: UndoActionPreferencesSchema.optional(),
  deleteOnUndo: z
    .boolean()
    .optional()
    .describe(
      'When enabled, prompt users to delete an account when undoing the last activity results in an empty activity history'
    ),
  hidePreviousActivity: z
    .array(RoleIdSchema)
    .optional()
    .describe('Hide account activity prior to the current assignment for these roles'),
  closerEventFlow: CloserEventFlowSchema.default({ type: 'residentFirst' }).optional(),
  mapControlKpis: z
    .array(ReportColumnDisplaySchema)
    .max(2)
    .optional()
    .describe('Up to 2 report KPI columns to display in the mobile map control alongside Active'),
  showAllCustomFieldsInResidentForm: z
    .boolean()
    .optional()
    .describe('Show all editable account custom fields in the mobile resident form'),
  defaultEventConfig: WorkflowEventConfigSchema.pick({ scheduleRestriction: true })
    .optional()
    .describe('Default schedule restriction applied when no stage/action-level scheduleRestriction is configured.'),
  rollback: WorkflowRollbackPreferencesSchema.optional().describe('Account stage rollback configuration'),
})
export type WorkflowPreferences = z.infer<typeof WorkflowPreferencesSchema>

export const UnsavedWorkflowSchema = z.object({
  companyId: CompanyIdSchema.optional(),
  name: z.string().nonempty().meta({ description: 'Name of the workflow' }),
  stages: z.array(WorkflowStageSchema),
  actions: z.array(WorkflowActionSchema),
  integrationMappings: z.array(WorkflowIntegrationMappingSchema).optional(),
  migrationMappings: z
    .array(WorkflowIntegrationMappingSchema)
    .optional()
    .describe(
      'Maps a legacy ID (`sourceStage`: an account status, disposition or event outcome ID, or a legacy metric such as `pitch`) to the stage or action that replaces it (`targetId`). Imports, CRM syncs, plans, programs and reports still read it to translate legacy IDs. Unrelated to checkpoints.'
    ),
  isMigrated: z
    .boolean()
    .optional()
    .describe(
      "Whether the company's legacy setup (account statuses, dispositions, event outcomes) has been fully migrated to this workflow. It does not say whether actions use `checkpoints` or `hooks.pre`. Read-only through `/workflow/update`."
    ),
  visibleTo: WorkflowEntityIdSchema.array().optional(),
  entity: WorkflowEntityTypeSchema.optional(),
  removeEntitlements: TerrosEntitlementSchema.array().optional(),
  preferences: WorkflowPreferencesSchema.optional(),
})
export type UnsavedWorkflow = z.infer<typeof UnsavedWorkflowSchema>

export const WorkflowDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedWorkflowSchema.shape,
  workflowId: WorkflowIdSchema,
  companyId: CompanyIdSchema,
})
export type WorkflowData = z.infer<typeof WorkflowDataSchema>

export const SmallWorkflowDataSchema = WorkflowDataSchema.pick({
  workflowId: true,
  companyId: true,
  stages: true,
  actions: true,
  migrationMappings: true,
})
export type SmallWorkflowData = z.infer<typeof SmallWorkflowDataSchema>

export const toSmallWorkflowData = (workflow: WorkflowData): SmallWorkflowData => {
  return {
    workflowId: workflow.workflowId,
    companyId: workflow.companyId,
    actions: workflow.actions,
    stages: workflow.stages,
    migrationMappings: workflow.migrationMappings,
  }
}

export const TinyWorkflowStateSchema = z.object({
  actionId: WorkflowActionIdSchema.optional(),
  stageId: WorkflowStageIdSchema,
  workflowId: WorkflowIdSchema,
  stageName: z.string().optional(),
  actionName: z.string().optional(),
  sourceStage: z.string().optional(),
})
export type TinyWorkflowState = z.infer<typeof TinyWorkflowStateSchema>

export const WorkflowSetupMigrationInputSchema = z.object({
  ...WithWorkflowIdSchema.shape,
  createDuplicateAccounts: z.boolean(),
})
export type WorkflowSetupMigrationInput = z.infer<typeof WorkflowSetupMigrationInputSchema>

export const WorkflowDataMigrationEnqueueInputSchema = z.object({
  ...WithWorkflowIdSchema.shape,
  resolveContacts: z.enum(['setter-closer', 'all']),
})
export type WorkflowDataMigrationEnqueueInput = z.infer<typeof WorkflowDataMigrationEnqueueInputSchema>

export const WorkflowDataMigrationRerunEnqueueInputSchema = z.object({
  ...WorkflowDataMigrationEnqueueInputSchema.shape,
  force: z.boolean().optional().describe('whether to include accounts with workflow ids'),
})
export type WorkflowDataMigrationRerunEnqueueInput = z.infer<typeof WorkflowDataMigrationRerunEnqueueInputSchema>

export const SunrunAccountMigrationInputSchema = z.object({
  ...WithWorkflowIdSchema.shape,
  teamIds: TeamIdSchema.array().min(1).describe('teams to migrate, including their downline'),
})
export type SunrunAccountMigrationInput = z.infer<typeof SunrunAccountMigrationInputSchema>

export type SunrunAccountMigrationState = {
  workflow: SmallWorkflowData
  teamIds: TeamId[]
}

export type UserCompanyWorkflowId = {
  userId: UserId
  companyId: CompanyId
  workflowId: WorkflowId
}

export type LocationCompanyWorkflowId = {
  locationId: LocationId
  companyId: CompanyId
  workflowId: WorkflowId
}

export type WorkflowDataMigrationInput = {
  workflow: SmallWorkflowData
} & Pick<WorkflowDataMigrationEnqueueInput, 'resolveContacts'> & {
    sortTimestamp?: SortTimestamp
  }

export const ActivitySortBySchema = z.enum(['distance', 'timestamp'])
export type ActivitySortBy = z.infer<typeof ActivitySortBySchema>

export const ActivitySortOrderSchema = z.enum(['asc', 'desc'])
export type ActivitySortOrder = z.infer<typeof ActivitySortOrderSchema>

export const ActivitySortCursorSchema = z
  .tuple([z.number(), z.string()])
  .describe('Activity pagination cursor containing the sort value followed by the activity ID')
export type ActivitySortCursor = z.infer<typeof ActivitySortCursorSchema>

export const WorkflowActivityFilterSchema = z.object({
  ...OptionalDateRangeSchema.shape,
  companyId: CompanyIdSchema,
  workflowId: WorkflowIdSchema,
  teamIds: TeamIdSchema.array().optional(),
  userIds: UserIdSchema.array().optional(),
  actionIds: WorkflowActionIdSchema.array().optional(),
  cities: z.string().array().optional().meta({ description: 'Filter by city names.' }),
  states: z.string().array().optional().meta({ description: 'Filter by state names.' }),
  zipCodes: z.string().array().optional().meta({ description: 'Filter by ZIP codes.' }),
  customFields: CustomFieldFilterSchema.optional().meta({ description: 'Filter by custom field values.' }),
  propertyCoordinates: LatLngSchema.array().optional(),
  sortTimestamp: ActivitySortCursorSchema.optional(),
  sortBy: ActivitySortBySchema.optional(),
  sortOrder: ActivitySortOrderSchema.optional(),
  query: z.string().optional(),
  size: z.number().min(10).max(1000).optional(),
})
export type WorkflowActivityFilter = z.infer<typeof WorkflowActivityFilterSchema>
